import { Box, SxProps, Theme } from "@mui/material";
import { motion, usePresenceData } from "motion/react";
import React, { Children } from "react";

import { AnimatePresence } from "./AnimatePresence";

const MotionBox = motion.create(Box);

interface FadeStackProps {
  children: React.ReactNode;
  duration?: number;
  sx?: SxProps<Theme>;
}

/**
 * Stacks its children on top of each other. The wrapper appears instantly
 * with its first child, and fades out (then unmounts) when the last child
 * leaves. Subsequent children added to a mounted wrapper fade in; removed
 * children fade out. Each child must have a stable key.
 */
export const FadeStack = ({ children, duration = 0.3, sx }: FadeStackProps) => {
  const childArray = Children.toArray(children).filter(React.isValidElement);

  return (
    <AnimatePresence>
      {childArray.length > 0 && (
        <MotionBox
          initial={false}
          exit={{ opacity: 0 }}
          transition={{ duration, ease: "easeInOut" }}
          sx={[
            { position: "relative", height: "100%", overflow: "hidden" },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        >
          <AnimatePresence>
            {childArray.map((child) => (
              <FadeItem key={child.key} duration={duration}>
                {child}
              </FadeItem>
            ))}
          </AnimatePresence>
        </MotionBox>
      )}
    </AnimatePresence>
  );
};

const FadeItem = ({
  duration,
  children,
}: {
  duration: number;
  children: React.ReactNode;
}) => {
  const initialRender = usePresenceData() as boolean;

  return (
    <motion.div
      initial={initialRender ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration, ease: "easeInOut" }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {children}
    </motion.div>
  );
};
